import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  HeaderClassParams,
  RowClassParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueParserFunc,
} from '@ag-grid-community/core';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { BehaviorSubject } from 'rxjs';

import { TableConstants } from '@shared/constants/table.constants';
import { listDriverPatientDistributionsQuery } from '@shared/services/gql.service';
import { decimalRoundingToString } from '@shared/utils';
import { Maybe, Utils } from '@shared/utils/utils';

import { PATIENT_CURVES_EXPORT_CONST } from './patinet-curves-export.const';
import { AgMonthCellComponent } from '../../../components/ag-month-cell/ag-month-cell.component';

export interface ComparisonCard {
  compareFrom: ForecastActualComparisonType;
  compareTo: ForecastActualComparisonType;
  title: string;
}

export interface ForecastActualComparisonType {
  percent: string;
  title: string;
  value: string;
}

export interface ForecastAndActualType {
  cumulative_enrollment_percentage: Maybe<number>;
  net_patients_enrolled: Maybe<number>;
  patients_complete: Maybe<number>;
  patients_discontinued: Maybe<number>;
  patients_enrolled: Maybe<number>;
  total_patients_enrolled: Maybe<number>;
}

export interface TopCard {
  title: string;
  value: string;
}

type Timeline = BehaviorSubject<{ endDate?: string; startDate?: string }>;

export const MIN_WIDTH = 90;

export const MAX_WIDTH = 170;

export const patientDriverMonthColumnDefs = (
  currentPatientGroupName$: BehaviorSubject<string>,
  timeline$: Timeline
): ColDef | ColGroupDef =>
  ({
    children: [
      {
        cellRenderer: AgMonthCellComponent,
        cellRendererParams: {
          noMonthInTimelineTooltipMessage:
            'This month is no longer included in the trial timeline. Please adjust your Patient Curve or Trial Timeline to fix this issue.',
          timeline$,
        },
        editable: false,
        field: 'distribution_month',
        headerClass: 'ag-header-cell',
        headerName: 'Month',
        minWidth: MIN_WIDTH * 2,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === 'TOTAL') {
            return 'TOTAL';
          }

          return params.value
            ? Utils.dateFormatter(params.value, { day: undefined, year: '2-digit' })
            : '';
        },
        width: MIN_WIDTH * 2,
      },
    ],
    colId: 'month',
    headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
    headerValueGetter: () => {
      return currentPatientGroupName$.getValue();
    },
  }) as ColGroupDef;

export const patientDriverEnrolledColumnDefs = (
  valueFormatter: ValueFormatterFunc,
  valueParser: ValueParserFunc,
  showEDCColumns: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      cellDataType: 'text',
      field: 'forecast.patients_enrolled',
      headerName: 'Forecast',
      minWidth: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
    },
    {
      editable: false,
      field: 'actual.patients_enrolled',
      headerName: 'EDC',
      hide: !showEDCColumns,
      minWidth: MIN_WIDTH,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: MIN_WIDTH,
    },
  ],
  colId: 'enrolled',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Enrolled',
});

export const patientDriverCompleteColumnDefs = (
  valueFormatter: ValueFormatterFunc,
  valueParser: ValueParserFunc,
  showEDCColumns: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      cellDataType: 'text',
      field: 'forecast.patients_complete',
      headerName: 'Forecast',
      minWidth: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
    },
    {
      editable: false,
      field: 'actual.patients_complete',
      headerName: 'EDC',
      hide: !showEDCColumns,
      minWidth: MIN_WIDTH,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: MIN_WIDTH,
    },
  ],
  colId: 'patients_complete',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Complete',
});

export const patientDriverDiscontinuedColumnDefs = (
  valueFormatter: ValueFormatterFunc,
  valueParser: ValueParserFunc,
  showEDCColumns: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      cellDataType: 'text',
      field: 'forecast.patients_discontinued',
      headerName: 'Forecast',
      minWidth: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
    },
    {
      editable: false,
      field: 'actual.patients_discontinued',
      headerName: 'EDC',
      hide: !showEDCColumns,
      minWidth: MIN_WIDTH,
      valueFormatter: valueFormatter,
      valueParser: valueParser,
      width: MIN_WIDTH,
    },
  ],
  colId: 'patients_discontinued',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Discontinued',
});

export const patientDriverNetEnrolledColumnDefs = (
  valueFormatter: ValueFormatterFunc,
  showEDCColumns: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      editable: false,
      field: 'forecast.net_patients_enrolled',
      headerName: 'Forecast',
      minWidth: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
      valueFormatter: valueFormatter,
      width: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
    },
    {
      editable: false,
      field: 'actual.net_patients_enrolled',
      headerName: 'EDC',
      hide: !showEDCColumns,
      minWidth: MIN_WIDTH,
      valueFormatter: valueFormatter,
      width: MIN_WIDTH,
    },
  ],
  colId: 'net_patients_enrolled',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Net Enrolled',
});

export const patientDriverTotalEnrolledColumnDefs = (
  valueFormatter: ValueFormatterFunc,
  showEDCColumns: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      editable: false,
      field: 'forecast.total_patients_enrolled',
      headerName: 'Forecast',
      minWidth: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
      valueFormatter: valueFormatter,
      width: showEDCColumns ? MIN_WIDTH : MIN_WIDTH * 2,
    },
    {
      editable: false,
      field: 'actual.total_patients_enrolled',
      headerName: 'EDC',
      hide: !showEDCColumns,
      minWidth: MIN_WIDTH,
      valueFormatter: valueFormatter,
      width: MIN_WIDTH,
    },
  ],
  colId: 'total_patients_enrolled',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Total Enrolled',
});

export const patientDriverAccrualPatientCurveColumnDefs = (
  editCell: boolean
): ColDef | ColGroupDef => ({
  children: [
    {
      cellClass: () => {
        return editCell
          ? ['opacity-70', 'cellPercent', 'justify-end']
          : ['justify-end', 'cellPercent'];
      },
      editable: false,
      field: 'forecast.cumulative_enrollment_percentage',
      headerName: 'Forecast',
      minWidth: MIN_WIDTH,
      valueFormatter: (val: ValueFormatterParams) => {
        return val.value ? `${decimalRoundingToString(val.value, 2)}%` : Utils.zeroHyphen;
      },
      width: MIN_WIDTH,
    },
  ],
  colId: 'cumulative_enrollment_percentage',
  headerClass: 'ag-header-align-center bg-aux-blue-dark aux-white border-aux-blue-dark',
  headerName: 'Accrual Curve',
  wrapText: true,
});

export class PatientCurvesConstants {
  static readonly GRID_OPTIONS = {
    enableRangeSelection: true,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    getRowStyle: (params: RowClassParams) => {
      if (params.node.rowPinned) {
        return { 'font-weight': 'bold' };
      }

      return {};
    },
    groupIncludeFooter: true,
    headerHeight: 50,
    onCellClicked(event: CellClickedEvent) {
      const cellRange = event.api.getCellRanges();

      if ((cellRange?.length || 0) > 1) {
        event.api.stopEditing();
      }
    },
    pinnedBottomRowData: [],
    rowClassRules: {
      'has-error': (params: RowClassParams) => params.data.showError,
    },
    suppressMenuHide: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
  };
  static readonly GRID_OPTIONS_EXEL_STYLES = PATIENT_CURVES_EXPORT_CONST;

  static allColDefs = ({
    currentPatientGroupName$,
    editCell,
    showEDCColumns,
    timeline$,
    valueFormatter,
    valueParser,
  }: {
    currentPatientGroupName$: BehaviorSubject<string>;
    editCell: boolean;
    showEDCColumns: boolean;
    timeline$: Timeline;
    valueFormatter: ValueFormatterFunc;
    valueParser: ValueParserFunc;
  }): ColDef[] | ColGroupDef[] => [
    {
      field: 'id',
      headerName: '',
      hide: true,
      valueFormatter: valueFormatter,
    },
    patientDriverMonthColumnDefs(currentPatientGroupName$, timeline$),
    TableConstants.SPACER_COLUMN,
    patientDriverEnrolledColumnDefs(valueFormatter, valueParser, showEDCColumns),
    TableConstants.SPACER_COLUMN,
    patientDriverDiscontinuedColumnDefs(valueFormatter, valueParser, showEDCColumns),
    TableConstants.SPACER_COLUMN,
    patientDriverCompleteColumnDefs(valueFormatter, valueParser, showEDCColumns),
    TableConstants.SPACER_COLUMN,
    patientDriverNetEnrolledColumnDefs(valueFormatter, showEDCColumns),
    TableConstants.SPACER_COLUMN,
    patientDriverTotalEnrolledColumnDefs(valueFormatter, showEDCColumns),
    TableConstants.SPACER_COLUMN,
    patientDriverAccrualPatientCurveColumnDefs(editCell),
  ];

  static getCellClasses = (editMode$: BehaviorSubject<boolean>) => (params: CellClassParams) => {
    const editCell = editMode$.getValue();

    if (params.data.distribution_month === 'TOTAL') {
      return params.colDef.field === 'distribution_month' ? '' : 'justify-end';
    }

    if (!params.colDef.field) {
      return '';
    }

    if (params.colDef.field.includes('distribution_month')) {
      return editCell
        ? ['opacity-70', 'justify-start', 'ag-cell-not-edit-cell']
        : ['justify-start'];
    }

    if (
      params.colDef.field.includes('forecast') &&
      (params.colDef.field.includes('patients_enrolled') ||
        params.colDef.field.includes('patients_discontinued') ||
        params.colDef.field.includes('patients_complete')) &&
      !params.colDef.field.includes('net') &&
      !params.colDef.field.includes('total')
    ) {
      return editCell
        ? ['editable-cell', 'justify-end', 'ag-cell-align-right']
        : ['justify-end', 'ag-cell-align-right'];
    }

    if (['actual', 'net', 'total'].some((x) => !!params.colDef.field?.includes(x))) {
      return editCell
        ? ['justify-end', 'ag-cell-align-right', 'ag-cell-not-edit-cell']
        : ['justify-end', 'ag-cell-align-right'];
    }

    return '';
  };

  static getHeaderClasses =
    (editMode$: BehaviorSubject<boolean>) =>
    (params: HeaderClassParams): string | string[] => {
      const editCell = editMode$.getValue();

      if (!params.column?.getColId()) {
        return '';
      }

      const colId = params.column.getColId();

      if (!colId.includes('forecast')) {
        return '';
      }

      if (['complete', 'discontinued', 'enrolled'].some((col) => colId.includes(col))) {
        return editCell &&
          !colId.includes('total') &&
          !colId.includes('net') &&
          !colId.includes('actual')
          ? ['ag-header-edit-mode']
          : [];
      }

      return '';
    };

  // eslint-disable-next-line max-lines-per-function
  static multiChartOptions(
    data: listDriverPatientDistributionsQuery[],
    labels: string[]
  ): ChartConfiguration<'line'> {
    const colors = [
      {
        border: true,
        color: '#E3B506',
      },
      {
        border: true,
        color: '#236262',
      },
      {
        border: false,
        color: '#095b95',
      },
      {
        border: false,
        color: '#6B9DBF',
      },
      {
        border: false,
        color: '#9dbdd5',
      },
    ].map(({ border, color }) => {
      return <ChartDataset<'line'>>{
        backgroundColor: color,
        borderColor: color,
        borderWidth: border ? 3 : 0,
        hoverBackgroundColor: color,
        hoverBorderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointHitRadius: 4,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: color,
        pointHoverBorderWidth: 4,
        pointHoverRadius: 4,
        pointRadius: 0,
        pointStyle: 'rectRounded',
      };
    });

    return {
      data: {
        datasets: [
          {
            ...colors[0],
            data: data.map((x) => x.forecast.net_patients_enrolled || 0),
            label: 'Net Patients Enrolled',
            type: 'line',
          },
          {
            ...colors[1],
            data: data.map((x) => x.forecast.total_patients_enrolled || 0),
            label: 'Total Patients Enrolled',
            type: 'line',
          },
          {
            ...colors[2],
            cubicInterpolationMode: 'monotone',
            data: data.map((x) => x.forecast.patients_enrolled || 0),
            label: 'Enrolled',
            // https://github.com/chartjs/Chart.js/issues/11356
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: 'bar',
          },
          {
            ...colors[3],
            cubicInterpolationMode: 'monotone',
            data: data.map((x) => x.forecast.patients_discontinued || 0),
            label: 'Discontinued',
            // https://github.com/chartjs/Chart.js/issues/11356
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: 'bar',
          },
          {
            ...colors[4],
            cubicInterpolationMode: 'monotone',
            data: data.map((x) => x.forecast.patients_complete || 0),
            label: 'Completed',
            // https://github.com/chartjs/Chart.js/issues/11356
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: 'bar',
          },
        ],
        labels,
      },
      legend: { bottom: true, left: false, right: false, top: false },
      options: {
        maintainAspectRatio: false,
        responsive: true,
      },
      type: 'line',
    };
  }
}
